import { Injectable, computed, inject, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { toSignal } from "@angular/core/rxjs-interop";
import { distinctUntilChanged, map } from "rxjs";
import { Trade } from "@app/common/types/platform.types";
import { parseYyyyMmDdToLocalDate } from "@app/common/utils/date-utils";
import dayjs from "dayjs/esm";
import utc from "dayjs/esm/plugin/utc";
import isSameOrAfter from "dayjs/esm/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/esm/plugin/isSameOrBefore";
import { BrokerServiceProvider } from "./brokers/broker-service-provider";

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

/**
 * Value equality for a {@link DateRange}: same instant (or same null-ness) on
 * both edges. Used as the `equal` for range signals so a re-emitted but
 * unchanged range keeps its existing object identity.
 */
function sameDateRange(a: DateRange, b: DateRange): boolean {
  return sameDate(a.start, b.start) && sameDate(a.end, b.end);
}

function sameDate(a: Date | null, b: Date | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aTime = a.getTime();
  const bTime = b.getTime();
  // An unparseable query param yields an Invalid Date; never treat two of
  // those as equal, so a genuinely bad value still propagates.
  if (Number.isNaN(aTime) || Number.isNaN(bTime)) return false;
  return aTime === bTime;
}

export type DateRangePreset =
  | "TODAY"
  | "7D"
  | "YTD"
  | "MTD"
  | "30D"
  | "90D"
  | "6M"
  | "1Y"
  | "WTD"
  | "ALL"
  | null;

@Injectable({
  providedIn: "root",
})
export class DateRangeService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private brokerProvider = inject(BrokerServiceProvider);

  // Track the active preset
  private _activePreset = signal<DateRangePreset>(null);

  // Convert query params to signals.
  //
  // Every query-param write on the page (cid, chart, layout, yscale, ...)
  // re-emits queryParamMap, so mapping straight to new Date objects published
  // a brand-new DateRange on each unrelated navigation. Downstream signal
  // inputs saw a changed reference and redrew charts for nothing (opening the
  // agent chat repainted the Experiments charts). Gate on the raw YYYY-MM-DD
  // strings first, then keep the previous Date instances when the range is
  // value-identical, so an unchanged range never publishes new objects.
  readonly dateRange = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => ({
        start: params.get("start"),
        end: params.get("end"),
      })),
      distinctUntilChanged(
        (previous, current) =>
          previous.start === current.start && previous.end === current.end
      ),
      map((params) => ({
        // Treat YYYY-MM-DD as a local calendar day (not UTC midnight)
        start: parseYyyyMmDdToLocalDate(params.start),
        end: parseYyyyMmDdToLocalDate(params.end),
      }))
    ),
    {
      initialValue: { start: null, end: null } as DateRange,
      equal: sameDateRange,
    }
  );

  // Check if range=ALL is set in query params
  readonly isAllRange = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => params.get("range") === "ALL")
    ),
    { initialValue: false }
  );

  // Computed properties for convenience
  readonly hasDateRange = computed(() => {
    const range = this.displayDateRange();
    return !!(range.start && range.end);
  });

  readonly startDate = computed(() => this.displayDateRange().start);
  readonly endDate = computed(() => this.displayDateRange().end);

  /**
   * Returns the currently active date range preset
   */
  activePreset(): DateRangePreset {
    return this._activePreset();
  }

  /**
   * Sets the date picker range for display only (used for ALL preset)
   * This is stored in a signal and doesn't change the URL
   */
  private _displayDateRange = signal<DateRange>({ start: null, end: null });

  readonly displayDateRange = computed(() => {
    const isAll = this.isAllRange();
    if (isAll) {
      return this._displayDateRange();
    }
    return this.dateRange();
  });

  /**
   * Sets the display date range for ALL preset after API response
   */
  setDisplayDateRange(startDate: Date, endDate: Date): void {
    const current = this._displayDateRange();
    const startDay = dayjs(startDate).format("YYYY-MM-DD");
    const endDay = dayjs(endDate).format("YYYY-MM-DD");
    const unchanged =
      current.start != null &&
      current.end != null &&
      dayjs(current.start).format("YYYY-MM-DD") === startDay &&
      dayjs(current.end).format("YYYY-MM-DD") === endDay;

    // Cached ALL-range consumers can reapply the same resolved bounds when a
    // dependent effect reruns. Avoid publishing a new signal object or
    // navigating again: either action can retrigger that consumer forever.
    if (unchanged) return;

    this._displayDateRange.set({ start: startDate, end: endDate });

    // For ALL preset, update the URL with actual start/end dates after getting trade data
    // Keep the range=ALL parameter and use Apollo cache to prevent duplicate API calls
    const isAll = this.isAllRange();
    if (isAll) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          start: startDay,
          end: endDay,
          range: "ALL", // Keep the range=ALL parameter
        },
        queryParamsHandling: "merge",
      });
    }
  }

  /**
   * Clears resolved ALL-range dates without changing the active URL preset.
   * Used while a newly selected portfolio is resolving its own data bounds.
   */
  clearDisplayDateRange(): void {
    this._displayDateRange.set({ start: null, end: null });

    const queryParams = this.route.snapshot.queryParams;
    if (this.isAllRange() && (queryParams["start"] || queryParams["end"])) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { start: null, end: null, range: "ALL" },
        queryParamsHandling: "merge",
      });
    }
  }

  /**
   * Checks if page should default to 30 days (no query params)
   */
  shouldDefaultTo30Days(): boolean {
    const params = this.route.snapshot.queryParamMap;
    return !params.has("start") && !params.has("end") && !params.has("range");
  }

  /**
   * Sets default 30-day range
   */
  setDefault30DayRange(): void {
    const now = dayjs();
    const startDate = now.subtract(30, "day").startOf("day").toDate();
    const endDate = now.endOf("day").toDate();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start: dayjs(startDate).format("YYYY-MM-DD"),
        end: dayjs(endDate).format("YYYY-MM-DD"),
        range: null, // Remove range=ALL for default range
      },
      queryParamsHandling: "merge",
    });
  }

  /**
   * Sets an explicit custom range and updates the canonical URL query params.
   * Use this for manual date picker selections and feature-driven date changes
   * that should behave like a user-selected global range.
   */
  setCustomDateRange(startDate: Date | string, endDate: Date | string): void {
    this._activePreset.set(null);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start: dayjs(startDate).format("YYYY-MM-DD"),
        end: dayjs(endDate).format("YYYY-MM-DD"),
        range: null,
      },
      queryParamsHandling: "merge",
    });
  }

  /**
   * Sets the active preset without triggering navigation (for internal use)
   */
  private _setActivePresetSilently(preset: DateRangePreset): void {
    this._activePreset.set(preset);
  }

  /**
   * Initializes the ALL preset after trade data is loaded
   * This is called from the trade service after setting the display date range
   */
  initializeAllPreset(): void {
    const isAll = this.isAllRange();
    if (isAll) {
      this._setActivePresetSilently("ALL");
    }
  }

  /**
   * Sets the active date range preset
   */
  setActivePreset(preset: DateRangePreset, vid?: any): void {
    // If vid is not provided, preserve the existing one from the current route
    if (vid === undefined) {
      vid = this.route.snapshot.queryParams['vid'];
    }
    
    this._activePreset.set(preset);

    // Calculate and apply the actual date range based on the preset
    if (preset) {
      const now = dayjs();
      let startDate: Date;
      const endDate = now.endOf("day").toDate(); // End date is always end of today local time for these presets

      switch (preset) {
        case "TODAY":
          // Start at beginning of today local time, end at end of today local time
          startDate = dayjs().startOf("day").toDate();
          const endDateToday = dayjs().endOf("day").toDate();

          // Update query params for TODAY to include full day
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              start: dayjs(startDate).format("YYYY-MM-DD"),
              end: dayjs(endDateToday).format("YYYY-MM-DD"),
              range: null,
              vid,
            },
            queryParamsHandling: "merge",
          });
          return;
          break;
        case "7D":
          startDate = dayjs().subtract(7, "day").startOf("day").toDate();
          break;
        case "YTD":
          startDate = dayjs().startOf("year").startOf("day").toDate();
          break;
        case "MTD":
          startDate = dayjs().startOf("month").startOf("day").toDate();
          break;
        case "30D":
          startDate = dayjs().subtract(30, "day").startOf("day").toDate();
          break;
        case "90D":
          startDate = dayjs().subtract(90, "day").startOf("day").toDate();
          break;
        case "6M":
          startDate = dayjs().subtract(6, "month").startOf("day").toDate();
          break;
        case "1Y":
          startDate = dayjs().subtract(1, "year").startOf("day").toDate();
          break;
        case "WTD":
          // Start date is the beginning of the current week (Sunday) in local time
          const dayOfWeek = now.day(); // 0 is Sunday, 1 is Monday, etc.
          startDate = now.subtract(dayOfWeek, "day").startOf("day").toDate();
          break;
        case "ALL":
          // The concrete first/last dates are resolved by the optimized
          // backend response. Clear any prior ALL display window so the UI
          // cannot briefly show stale dates from another portfolio/range.
          this.clearDisplayDateRange();
          // For ALL preset, set range=ALL query parameter
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              range: "ALL",
              start: null,
              end: null,
              vid,
            },
            queryParamsHandling: "merge",
          });
          return; // Exit early since we've set the range param
          break;
        default:
          return; // If preset is not recognized, don't change the date range
      }

      // Update query params with calculated date range
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          start: dayjs(startDate).format("YYYY-MM-DD"),
          end: dayjs(endDate).format("YYYY-MM-DD"),
          range: null, // Remove range=ALL for all non-ALL presets
          vid,
        },
        queryParamsHandling: "merge",
      });
    }
  }

  /**
   * Get the full time span of all available trades
   * @returns DateRange object with earliest and latest trade dates
   */
  getFullTradeTimespan(): DateRange {
    const trades = this.brokerProvider.activeBrokerService.allTradeData();

    if (!trades || trades.length === 0) {
      return { start: null, end: null };
    }

    // The trades are already sorted by date in the SchwabService.allTradeData() method
    // So we can just get the first and last elements
    const firstTrade = trades[0];
    const lastTrade = trades[trades.length - 1];

    return {
      start: new Date(firstTrade.date),
      end: new Date(lastTrade.date),
    };
  }

  /**
   * Get the date range for the most recent trades up to a specified maximum
   * If there are <= maxTrades trades, returns the range from first to last trade
   * If there are > maxTrades trades, returns the range from the maxTrades most recent trade to the most recent trade
   * If maxTrades is -1, returns the full range for all trades regardless of count
   * @param trades Array of trades to analyze
   * @param maxTrades Maximum number of trades to consider for the date range, or -1 for all trades
   * @returns DateRange object with start and end dates for the recent trades
   */
  getMaxTradesDateRange(trades: Trade[], maxTrades: number): DateRange {
    const tradesToAnalyze = trades;

    if (!tradesToAnalyze || tradesToAnalyze.length === 0) {
      return { start: null, end: null };
    }

    // Sort trades by date to ensure proper ordering
    const sortedTrades = [...tradesToAnalyze].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // If maxTrades is -1, return the full range for all trades
    if (maxTrades === -1) {
      const firstTrade = sortedTrades[0];
      const lastTrade = sortedTrades[sortedTrades.length - 1];
      return {
        start: new Date(firstTrade.date),
        end: new Date(lastTrade.date),
      };
    }

    // If we have maxTrades or fewer trades, use the full range
    if (sortedTrades.length <= maxTrades) {
      const firstTrade = sortedTrades[0];
      const lastTrade = sortedTrades[sortedTrades.length - 1];
      return {
        start: new Date(firstTrade.date),
        end: new Date(lastTrade.date),
      };
    }

    // If we have more than maxTrades trades, take the most recent maxTrades
    // Since trades are sorted by date, we take from (length - maxTrades) to (length - 1)
    const startIndex = sortedTrades.length - maxTrades;
    const recentTrades = sortedTrades.slice(startIndex);

    const firstRecentTrade = recentTrades[0];
    const lastRecentTrade = recentTrades[recentTrades.length - 1];

    return {
      start: new Date(firstRecentTrade.date),
      end: new Date(lastRecentTrade.date),
    };
  }

  /**
   * Sets the date range to match the actual range of recently uploaded trades
   * This is called after trade upload to set the UI to show the actual trade date range
   * @param dateRange Optional pre-calculated date range. If not provided, calculates from existing trades
   */
  setRangeToRecentTrades(dateRange?: DateRange): void {
    const recentTradesDateRange =
      dateRange ||
      this.getMaxTradesDateRange(
        this.brokerProvider.activeBrokerService.allTradeData(),
        1000
      );

    if (recentTradesDateRange.start && recentTradesDateRange.end) {
      this.setCustomDateRange(
        recentTradesDateRange.start,
        recentTradesDateRange.end
      );
    } else {
      // Fallback to 30D if no trades are available
      this.setActivePreset("30D");
    }
  }

  // Helper method to filter trades by date range
  filterTradesByDateRange(trades: Trade[]): Trade[] {
    const isAll = this.isAllRange();

    // If range=ALL, return all trades without filtering
    if (isAll) {
      return trades;
    }

    const dateRange = this.dateRange();

    if (!dateRange.start || !dateRange.end) {
      return trades;
    }

    return trades.filter((trade) => {
      // Match TradeDataService.getTradeHistory():
      // normalize selected calendar days in LOCAL time, then compare in UTC.
      const tradeDate = dayjs(trade.date).utc();
      const startDate = dayjs(dateRange.start).startOf("day").utc();
      const endDate = dayjs(dateRange.end).endOf("day").utc();
      return (
        tradeDate.isSameOrAfter(startDate) && tradeDate.isSameOrBefore(endDate)
      );
    });
  }
}
